import {weakMemo, singleMemo} from '@packages/utilities/memo';
import {
  type Qualified,
  namesEqual,
  getPluginName,
  qualify,
} from '@packages/systems/wfdl-Qualified';
import {
  matchExpression,
  Expressions,
  Plugins,
  Component,
  ExpressionPath,
} from '@packages/systems/wfdl';
import {
  constant,
  constantFalse,
  constantIdentity,
} from '@packages/utilities/fp/utils';
import {
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {pageComponentType} from './PageComponent';
import {CHILDREN_PATH, contains} from './ElementTreeUtils';
import {matchesDynContextConstant} from '@packages/systems/dynamo/utils/DynamoItemUtils';
import {isCollectionListPropsFilterTypeName} from '@packages/systems/dynamo/utils/CollectionListPropNameUtils';

const getExprElementId = matchExpression({
  Element: Expressions.getElementId,
  _: (_) => undefined,
});

// TODO namespace componentMapRootElementIds to account for possible duplicate
// ids in rootComponent.render expressions
/**
 * Returns a set of Component ids from componentMap excluding page
 */
const componentMapRootElementIds = weakMemo(
  (componentMap: Component.ComponentMap): Set<string> =>
    componentMap.reduce(
      (acc, component, name: Qualified<Component.ComponentName>) => {
        if (namesEqual(name, pageComponentType)) {
          return acc;
        }
        const render = Component.getRender(component);
        const elId = getExprElementId(render);

        return elId ? acc.add(elId) : acc;
      },
      new Set()
    )
);

/**
 * Returns true if the provided element is a Component root.
 */
export const isComponentRoot = weakMemo(
  (componentMap: Component.ComponentMap) => {
    const ids = componentMapRootElementIds(componentMap);
    return weakMemo((element: ExpressionElement): boolean =>
      ids.has(element.id)
    );
  }
);

/*
 * This functions checks to see if the provided element is a User Component instance.
 */
export const isUserComponentInst = (
  components: Component.ComponentMap,
  element: ExpressionElement
): boolean =>
  getPluginName(element.type) === '__SitePlugin' &&
  components.has(element.type);

/**
 * Returns true if the element is an instance of a component in the
 * componentMap.
 */
export const isComponentInst = weakMemo(
  (componentMap: Component.ComponentMap) =>
    weakMemo((element: ExpressionElement): boolean =>
      Boolean(componentMap.get(element.type))
    )
);

/**
 * Returns the root element of a Component if it's found from ComponentMap
 */
export function getComponentRootElement(
  componentMap: Component.ComponentMap,
  componentType: Plugins.QualifiedElement
): ExpressionElement | null {
  const component = componentMap.get(componentType);
  return component
    ? Expressions.getElement(Component.getRender(component))
    : null;
}

/**
 * Returns an element authored on a Component, whether it sits in the render or
 * in the reified slot-default content on `defaultData`.
 */
export function getComponentElementById(
  component: Component.ComponentType,
  elementId: string
): ExpressionElement | null {
  const element = Expressions.getElementById(
    Component.getRender(component),
    elementId
  );
  if (element) return element;

  const defaultData = Component.getDefaultData(component);
  if (!defaultData) return null;
  return Expressions.getElementById(defaultData, elementId);
}

/**
 * Returns a Component instance's root element
 */
export const getComponentInstRootElement: (
  componentMap: Component.ComponentMap
) => (element: ExpressionElement) => ExpressionElement | null | undefined =
  weakMemo((componentMap: Component.ComponentMap) =>
    weakMemo(
      (element: ExpressionElement): ExpressionElement | null | undefined => {
        return getComponentRootElement(componentMap, element.type);
      }
    )
  );

/**
 * Returns true if the element is an instance of a component in the
 * componentMap.
 */
export const isComponent: (
  componentMap: Component.ComponentMap
) => (element: ExpressionElement) => boolean = weakMemo(
  (componentMap: Component.ComponentMap) =>
    weakMemo(
      (element: ExpressionElement): boolean =>
        isComponentRoot(componentMap)(element) ||
        isComponentInst(componentMap)(element)
    )
);

export function isUserComponentRoot(
  componentMap: Component.ComponentMap,
  element: ExpressionElement
): boolean {
  return isUserComponentRootId(componentMap, element.id);
}

export function isUserComponentRootId(
  componentMap: Component.ComponentMap,
  id: string
): boolean {
  return getUserComponentRootsIds(componentMap).has(id);
}

const getUserComponentRootsIds = singleMemo(
  (componentMap: Component.ComponentMap): Set<string> => {
    return componentMap.reduce(userComponentRootIdsReducer, new Set());
  }
);

function userComponentRootIdsReducer(
  userComponentRootsIds: Set<string>,
  component: Component.ComponentType,
  componentName: Qualified<Component.ComponentName>
): Set<string> {
  if (isUserComponent(componentName)) {
    const root = Expressions.getElement(Component.getRender(component));
    if (root) {
      userComponentRootsIds.add(root.id);
    }
  }
  return userComponentRootsIds;
}

export function isUserComponent(
  componentName: Qualified<Component.ComponentName>
): boolean {
  return (
    getPluginName(componentName) === '__SitePlugin' &&
    !namesEqual(componentName, pageComponentType)
  );
}

export function closestComponentRoot(
  element: ExpressionElement,
  root: Expression,
  componentMap: Component.ComponentMap
): ExpressionElement | null {
  return (
    Expressions.closestElement(
      root,
      Expressions.getElementId(element),
      isComponentRoot(componentMap)
    ) || null
  );
}

const isChildrenListOfElements = matchExpression({
  List: (items) => items.every(Expressions.isElement),
  _: constantFalse,
});

export const isChildrenBound = (element: ExpressionElement): boolean => {
  const children = Expressions.getIn(
    Expressions.getElementData(element),
    CHILDREN_PATH
  );
  // TODO use instead `isExpressionBound` when it's available.
  // Tracking issue here: https://webflow.atlassian.net/browse/DESXP-3050
  return children ? !isChildrenListOfElements(children) : false;
};

export const containsUserComponentRoot = (
  element: ExpressionElement,
  componentMap: Component.ComponentMap
): boolean => contains(element, (el) => isUserComponentRoot(componentMap, el));

const filterValuePath: ExpressionPath = [{in: 'Record', at: 'value'}];

const elementDataMatcher = matchExpression({
  Distinct(distinct) {
    if (isCollectionListPropsFilterTypeName(distinct.name)) {
      const filter = Expressions.getRecord(distinct.value);
      const fields = filter?.fields ? Expressions.getList(filter.fields) : null;

      const hasContextualFilter = fields?.some((field) => {
        const filterValue = Expressions.getIn(field, filterValuePath);
        const filterValueText = filterValue
          ? Expressions.getText(filterValue)
          : null;
        return filterValueText && matchesDynContextConstant(filterValueText);
      });

      if (hasContextualFilter) {
        return Expressions.stop(true);
      }
    }

    return Expressions.resume(false);
  },
  Element: constant(Expressions.stop(false)),
  _: constant(Expressions.resume(false)),
});

const elementDataHasContextualFilterProp =
  Expressions.everything<boolean>(constantIdentity)(elementDataMatcher);

export const isUserComponentInstWithContextualFilterProp = (
  element: ExpressionElement
): boolean => {
  if (!isUserComponent(element.type)) {
    // Only User Component instances can have the Filter props.
    return false;
  }
  const elementData = Expressions.getElementData(element);
  return elementDataHasContextualFilterProp(elementData);
};

/**
 * Checks if an element is a Code Island component
 */
export function isCodeIsland(element: ExpressionElement): boolean {
  return namesEqual(
    element.type,
    qualify(Plugins.name('Code'), Component.name('CodeIsland'))
  );
}

/**
 * Checks if a render expression contains a Code Island component
 */
export const isCodeComponentRender = weakMemo((render: Expression): boolean => {
  return Expressions.some(
    matchExpression({
      Element: (element) => isCodeIsland(element),
      _: () => false,
    })
  )(render);
});

/**
 * Checks if a User Component contains a Code Island component
 * by traversing its render expression
 */
export const isCodeComponent = weakMemo(
  (componentMap: Component.ComponentMap) =>
    weakMemo((componentName: Qualified<Component.ComponentName>): boolean => {
      const component = componentMap.get(componentName);
      if (!component) {
        return false;
      }

      // Get the component's render expression
      const render = Component.getRender(component);

      return isCodeComponentRender(render);
    })
);

/**
 * Checks if a User Component is a Code Component and has a source
 */
export const isCodeComponentWithSource = weakMemo(
  (componentMap: Component.ComponentMap) =>
    weakMemo((componentName: Qualified<Component.ComponentName>): boolean => {
      const component = componentMap.get(componentName);
      if (!component) {
        return false;
      }

      const render = Component.getRender(component);
      if (!isCodeComponentRender(render)) {
        return false;
      }

      return Boolean(Component.getSource(component));
    })
);

/**
 * Checks if a ComponentType is a Code Component and has a source.
 */
export const isCodeComponentWithSourceComponent = (
  component: Component.ComponentType | null | undefined
): boolean => {
  if (!component) {
    return false;
  }

  const render = Component.getRender(component);
  if (!isCodeComponentRender(render)) {
    return false;
  }

  return Boolean(Component.getSource(component));
};

/**
 * Returns true if element is a read-only Code Component instance.
 */
export function isCodeComponentReadOnlyInst(
  componentMap: Component.ComponentMap,
  element: ExpressionElement | null | undefined
): boolean {
  if (!element) {
    return false;
  }

  const isCodeComponentInstResult = isCodeComponentInst(componentMap)(element);
  if (!isCodeComponentInstResult) {
    return false;
  }

  return !isCodeComponentInstWithSource(componentMap)(element);
}

/**
 * Returns true if a Code Component type is editable (has source).
 */
function isCodeComponentEditableComponent(
  component: Component.ComponentType | null | undefined
): boolean {
  return isCodeComponentWithSourceComponent(component);
}

/**
 * Returns true if a Code Component type should be treated as read-only.
 */
export function isCodeComponentReadOnlyComponent(
  component: Component.ComponentType | null | undefined
): boolean {
  if (!component) {
    return false;
  }

  const render = Component.getRender(component);
  if (!isCodeComponentRender(render)) {
    return false;
  }

  return !isCodeComponentEditableComponent(component);
}

/**
 * Checks if a User Component is a Library Component Local Copy
 * (i.e., it has an origin from another plugin)
 */
export const isLibraryComponent = weakMemo(
  (componentMap: Component.ComponentMap) =>
    weakMemo((componentName: Qualified<Component.ComponentName>): boolean => {
      const component = componentMap.get(componentName);
      if (!component) {
        return false;
      }

      // Check if the component has an origin (indicating it's from a library)
      return Component.getOrigin(component) !== undefined;
    })
);

/**
 * Checks if a User Component instance is a Code Component
 */
export const isCodeComponentInst = weakMemo(
  (componentMap: Component.ComponentMap) =>
    weakMemo((element: ExpressionElement): boolean => {
      // Only check user component instances
      if (!isUserComponentInst(componentMap, element)) {
        return false;
      }

      return isCodeComponent(componentMap)(element.type);
    })
);

/**
 * Checks if a User Component instance is a Code Component and has a source
 */
export const isCodeComponentInstWithSource = weakMemo(
  (componentMap: Component.ComponentMap) =>
    weakMemo((element: ExpressionElement) => {
      if (!isUserComponentInst(componentMap, element)) {
        return false;
      }

      return isCodeComponentWithSource(componentMap)(element.type);
    })
);

/**
 * Checks if a User Component instance is an editable Code Component
 */
export const isEditableCodeComponentInst = isCodeComponentInstWithSource;

/**
 * Returns a record of all Code Component existing in the __SitePlugin
 */
export const getLocalCodeComponents = weakMemo(
  (componentMap: Component.ComponentMap) => {
    return componentMap.reduceAtPluginName(
      Plugins.name('__SitePlugin'),
      (acc, component, key) => {
        if (isCodeComponentRender(Component.getRender(component))) {
          acc[key] = component;
        }
        return acc;
      },
      {} as Record<string, Component.ComponentType>
    );
  }
);
